// create a context for the registration setup

import { RegistrationTypes, Tax } from '@sportninja/common/types/Registration'
import req from '@sportninja/common/api/request'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { useWaivers, Waiver } from '@sportninja/common/hooks/useWaivers'
import { useAccount, AccountData } from '@sportninja/common/hooks/useAccount'
import {
  CustomForm,
  FormItem,
  Group,
  useCustomForm,
} from '@sportninja/common/hooks/useCustomForm'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { sanitizeContentForSave } from 'src/utils/sanitizeContentForSave'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import type { RegistrationAdminData } from '@sportninja/common/services/registration/getRegistrationAdmin'
import type { ProcessingFeeBearer } from '../helpers/processingFees'
import { useRegistrationAdmin } from '../hooks/useRegistrationAdmin'
import {
  hydrateFromAdmin,
  isInitialAdminLoad,
} from '../helpers/hydrateFromAdmin'

type RegistrationSetupState = {
  loading: boolean
  competitionDateRange: {
    starts_at: string | null
    ends_at: string | null
  }
  registrationType: 'player' | 'team'
  paidBy: 'player' | 'team'
  bccEmails: string[]
  currency_country_id: string
  taxes: string[]
  customBackgroundImage: string
  customBackgroundImageChanged: boolean
  darkMode: boolean
  waivers: string[]
  registrationForm: {
    [key: string]: boolean
  }
  fieldErrors: string[]
  customError: any
  payment_type: string
  autoApproveRegistrations: boolean
  enableTeamRegistration: boolean
  enablePlayerRegistration: boolean
  payment_provider: string | null
  show_waiver: boolean
  addPlayersOutsideOfRegistration: boolean
  player_form: CustomForm | null
  team_form: CustomForm | null
  headerContent: string
  teamConfirmationEmailContent?: string | null
  playerConfirmationEmailContent?: string | null
  // SN-8002 processing fees (competition-wide).
  paymentProcessingFeeBearer: ProcessingFeeBearer
  paymentProcessingFeeVisible: boolean
  /**
   * UI-only master switch. The API has no enable flag — when this is false the
   * payload sends null for both league fee amounts.
   */
  leagueProcessingFeeEnabled: boolean
  leagueProcessingFeePercent: number | null
  leagueProcessingFeeFlat: number | null
  leagueProcessingFeeVisible: boolean
  /**
   * When on, refunds return the registrant's full payment and the provider's
   * processing fee is charged to the league's account instead.
   */
  refundPaymentProcessingFee: boolean
}

type RegistrationSetupContextType = {
  state: RegistrationSetupState
  setState: React.Dispatch<React.SetStateAction<RegistrationSetupState>>
  registrationTypes: RegistrationTypes
  /** Selectable tax catalogue, from the admin payload. */
  availableTaxes: Tax[]
  submitRegistration: () => Promise<void>
  isUpdated: boolean
  // Account-level waiver catalogue. Distinct from the registration's *selected*
  // waivers (state.waivers) — the picker needs the full list to choose from.
  apiWaivers: Waiver[]
  refetchWaivers: () => void
  apiAccount: AccountData | null
  registrationId: string | null
  // Single source of truth for the setup form (GET /registration/:id/admin).
  registrationAdmin: RegistrationAdminData | null
  refetchRegistrationAdmin: () => void
  /** True only on the cold initial fetch — drives the skeleton. */
  isInitialLoading: boolean
  /** Locks the currency field once the competition has taken registrations. */
  hasRegistrations: boolean
  setCustomErroByField: (field: string, message: string) => void
  clearCustomErrors: () => void
  createPlayerForm: () => Promise<CustomForm | null>
  createTeamForm: () => Promise<CustomForm | null>
  // Patch state.team_form / state.player_form in place. Used by the form
  // components to apply mutation responses without triggering a registration
  // refetch (which would re-run the hydration effect and clobber unrelated
  // state).
  patchTeamForm: (transform: (form: CustomForm) => CustomForm) => void
  patchPlayerForm: (transform: (form: CustomForm) => CustomForm) => void
  // Lean mutation helpers that issue the PUT/POST/DELETE only. Replaces the
  // legacy `useCustomForm` mutations which always followed each request with
  // a redundant `GET /forms/{id}` to refresh the hook's internal state — the
  // form components don't read that state, so the GET was wasted bandwidth.
  // Each component applies the response via patchTeamForm / patchPlayerForm.
  formMutations: {
    createGroup: (data: any) => Promise<Group>
    updateGroup: (groupId: string, data: any) => Promise<Group>
    deleteGroup: (groupId: string) => Promise<void>
    reorderGroups: (
      formId: string,
      groups: Array<{ id: string; index: number }>
    ) => Promise<void>
    createItem: (groupId: string, data: any) => Promise<FormItem>
    updateItem: (itemId: string, data: any) => Promise<FormItem>
    deleteItem: (itemId: string) => Promise<void>
    reorderItems: (
      groupId: string,
      items: Array<{ id: string; index: number }>
    ) => Promise<void>
  }
  scheduleId: string
  activeTab: string
  setActiveTab: React.Dispatch<React.SetStateAction<string>>
}

const RegistrationSetupContext = createContext<
  RegistrationSetupContextType | undefined
>(undefined)

export const useRegistrationSetupContext = () => {
  const context = useContext(RegistrationSetupContext)
  if (!context) {
    throw new Error(
      'useRegistrationSetupContext must be used within a RegistrationSetupContextWrapper'
    )
  }
  return context
}

type RegistrationSetupProps = {
  registrationTypes: RegistrationTypes
  scheduleId: string
  accountId: string
  isRegistrationFormOpen: boolean
}

type RegistrationSetupContextWrapperProps = RegistrationSetupProps & {
  children: React.ReactNode
}

const RegistrationSetupContextWrapper = ({
  children,
  registrationTypes,
  scheduleId,
  accountId,
  isRegistrationFormOpen = false,
}: RegistrationSetupContextWrapperProps) => {
  const [state, setState] = useState<RegistrationSetupState>({
    show_waiver: false,
    competitionDateRange: {
      starts_at: '',
      ends_at: '',
    },
    registrationType: 'team',
    paidBy: 'player',
    bccEmails: [],
    currency_country_id: '',
    taxes: [],
    customBackgroundImage: '',
    customBackgroundImageChanged: false,
    darkMode: false,
    waivers: [],
    registrationForm: {},
    fieldErrors: [],
    customError: '',
    loading: false,
    payment_type: '1',
    autoApproveRegistrations: isCanlan ? false : true,
    enableTeamRegistration: true,
    enablePlayerRegistration: true,
    addPlayersOutsideOfRegistration: false,
    payment_provider: null,
    player_form: null,
    team_form: null,
    headerContent: '',
    teamConfirmationEmailContent: '',
    playerConfirmationEmailContent: '',
    paymentProcessingFeeBearer: 'league',
    paymentProcessingFeeVisible: true,
    leagueProcessingFeeEnabled: false,
    leagueProcessingFeePercent: null,
    leagueProcessingFeeFlat: null,
    leagueProcessingFeeVisible: true,
    refundPaymentProcessingFee: false,
  })

  const [activeTab, setActiveTab] = useState('0')

  const { data: apiWaivers, refetch: refetchWaivers } = useWaivers(accountId)

  // Retained for the two fields the admin payload doesn't carry: the account
  // timezone (GeneralSettings) and the provider fee rates (ProcessingFees).
  const { data: apiAccount, refetch: refetchAccount } = useAccount(accountId)

  const {
    data: registrationAdmin,
    isLoading: registrationAdminLoading,
    refetch: refetchRegistrationAdmin,
  } = useRegistrationAdmin({
    scheduleId,
    enabled: isRegistrationFormOpen,
  })

  const { createForm } = useCustomForm()
  const alreadyCreatedPlayerForm = useRef(false)
  const alreadyCreatedTeamForm = useRef(false)

  useEffect(() => {
    if (isRegistrationFormOpen) {
      refetchAccount()
    }
  }, [isRegistrationFormOpen, refetchAccount])

  const isUpdated = useMemo(
    () => Boolean(registrationAdmin?.id),
    [registrationAdmin?.id]
  )

  const availableTaxes = useMemo(
    () => registrationAdmin?.available_taxes ?? [],
    [registrationAdmin?.available_taxes]
  )

  const isInitialLoading = isInitialAdminLoad(
    registrationAdminLoading,
    registrationAdmin
  )

  // Absent (older API build) is treated as "has registrations" so the currency
  // field stays locked — failing safe, since wrongly unlocking it would let an
  // admin change currency on a competition that has already taken payments.
  const hasRegistrations = registrationAdmin
    ? registrationAdmin.has_registrations !== false
    : false

  const {
    createRegistration,
    updateRegistration,
    uploadRegistrationImage,
    deleteRegistrationImage,
  } = useRegistrationData('')

  // Helper function to update registration with form IDs only
  const updateRegistrationWithFormId = useCallback(
    async (formType: 'player' | 'team', formId: string) => {
      if (!isUpdated) {
        return // Only update if registration already exists
      }

      try {
        const updatePayload = {
          scheduleId: scheduleId,
          allowPlayerRegistration: state.enablePlayerRegistration,
          registrationType:
            state.enableTeamRegistration && !state.enablePlayerRegistration
              ? 'team'
              : 'player',
          enableRegistration: true,
          eventNumber: '',
          currency_country_id: state.currency_country_id,
          costPerRegistration: 0,
          paymentMethod: Number(state.payment_type),
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id:
            formType === 'player' ? formId : state?.player_form?.id || null,
          team_form_id:
            formType === 'team' ? formId : state?.team_form?.id || null,
          header_content: state.headerContent
            ? sanitizeContentForSave(state.headerContent)
            : null,
          team_confirmation_email_content: state.enableTeamRegistration
            ? sanitizeContentForSave(state.teamConfirmationEmailContent || '')
            : null,
          player_confirmation_email_content: state.enablePlayerRegistration
            ? sanitizeContentForSave(state.playerConfirmationEmailContent || '')
            : null,
          payment_processing_fee_bearer: state.paymentProcessingFeeBearer,
          payment_processing_fee_visible: state.paymentProcessingFeeVisible,
          league_processing_fee_percent: state.leagueProcessingFeeEnabled
            ? state.leagueProcessingFeePercent
            : null,
          league_processing_fee_flat: state.leagueProcessingFeeEnabled
            ? state.leagueProcessingFeeFlat
            : null,
          league_processing_fee_visible: state.leagueProcessingFeeVisible,
          refund_payment_processing_fee: state.refundPaymentProcessingFee,
        }

        await updateRegistration(updatePayload)
      } catch (error) {
        console.error(
          `❌ updateRegistrationWithFormId: Failed to update registration with ${formType} form ID:`,
          {
            error,
            formType,
            formId,
            scheduleId,
            isUpdated,
          }
        )
        // Don't throw error here to avoid breaking the form creation flow
      }
    },
    [isUpdated, updateRegistration, scheduleId, state]
  )

  const createPlayerForm = useCallback(async () => {
    if (state.player_form) {
      return state.player_form
    }

    if (alreadyCreatedPlayerForm.current) {
      return state.player_form
    }

    try {
      const newForm = await createForm({
        name: `${scheduleId}_player_form`,
        id: `${scheduleId}_player_form`,
      })

      alreadyCreatedPlayerForm.current = true

      // Update state with the new form
      setState((prev) => {
        return { ...prev, player_form: newForm }
      })

      // Immediately update the registration with the new form ID
      if (newForm?.id) {
        await updateRegistrationWithFormId('player', newForm.id)
      }
      return newForm
    } catch (error) {
      console.error('❌ createPlayerForm: Error during player form creation', {
        error,
        scheduleId,
        isUpdated,
      })
      throw error
    }
  }, [
    state.player_form,
    createForm,
    scheduleId,
    setState,
    updateRegistrationWithFormId,
    isUpdated,
  ])

  const createTeamForm = useCallback(async () => {
    if (alreadyCreatedTeamForm.current) {
      return state.team_form
    }

    if (state.team_form) {
      return state.team_form
    }

    try {
      const newForm = await createForm({
        name: `${scheduleId}_team_form`,
        id: `${scheduleId}_team_form`,
      })

      alreadyCreatedTeamForm.current = true

      // Update state with the new form
      setState((prev) => {
        return { ...prev, team_form: newForm }
      })

      // Immediately update the registration with the new form ID
      if (newForm?.id) {
        await updateRegistrationWithFormId('team', newForm.id)
      }

      return newForm
    } catch (error) {
      console.error('❌ createTeamForm: Error during team form creation', {
        error,
        scheduleId,
        isUpdated,
      })
      throw error
    }
  }, [
    state.team_form,
    createForm,
    scheduleId,
    setState,
    updateRegistrationWithFormId,
    isUpdated,
  ])

  const setCustomErroByField = (field: string, message: string) => {
    setState((prev) => ({ ...prev, fieldErrors: [...prev.fieldErrors, field] }))
    setState((prev) => ({
      ...prev,
      customError: { ...prev.customError, [field]: message },
    }))
  }

  const clearCustomErrors = () => {
    setState((prev) => ({ ...prev, fieldErrors: [], customError: {} }))
  }

  const setCustomErrors = (invalid_fields: any) => {
    if (invalid_fields.starts_at || invalid_fields.ends_at) {
      setCustomErroByField(
        'competitionDateRange',
        invalid_fields.starts_at || invalid_fields.ends_at
      )
    }
    if (invalid_fields.waivers) {
      setCustomErroByField('waivers', invalid_fields.waivers)
    }
    if (invalid_fields.name || invalid_fields.name_full) {
      setCustomErroByField('name', invalid_fields.name)
    }
    if (invalid_fields.currency_country_id) {
      setCustomErroByField(
        'currency_country_id',
        invalid_fields.currency_country_id
      )
    }
    if (invalid_fields.payment_type) {
      setCustomErroByField('payment_type', invalid_fields.payment_type)
    }
    if (invalid_fields.league_processing_fee_percent) {
      setCustomErroByField(
        'leagueProcessingFeePercent',
        invalid_fields.league_processing_fee_percent
      )
    }
    if (invalid_fields.league_processing_fee_flat) {
      setCustomErroByField(
        'leagueProcessingFeeFlat',
        invalid_fields.league_processing_fee_flat
      )
    }
  }

  const submitRegistration = async () => {
    try {
      setState((prev) => ({
        ...prev,
        fieldErrors: [],
        customError: '',
        loading: true,
      }))

      if (
        !state.competitionDateRange.starts_at ||
        !state.competitionDateRange.ends_at
      ) {
        const error = new Error(
          'Please select a registration start and end date'
        ) as Error & {
          invalid_fields?: Record<string, string>
        }
        error.invalid_fields = {
          starts_at: 'Please select a registration start and end date',
        }
        setActiveTab('0')
        throw error
      }

      if (state.show_waiver && state.waivers.length === 0) {
        const error = new Error(
          'Please select at least one waiver'
        ) as Error & {
          invalid_fields?: Record<string, string>
        }
        error.invalid_fields = { waivers: 'Please select at least one waiver' }
        setActiveTab('2')
        throw error
      }

      // Handle registration data
      if (isUpdated) {
        await updateRegistration({
          scheduleId: scheduleId,
          allowPlayerRegistration: state.enablePlayerRegistration,
          registrationType:
            state.enableTeamRegistration && !state.enablePlayerRegistration
              ? 'team'
              : 'player',
          enableRegistration: true,
          eventNumber: '',
          currency_country_id: state.currency_country_id,
          costPerRegistration: 0,
          paymentMethod: Number(state.payment_type),
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id: state?.player_form?.id || null,
          team_form_id: state?.team_form?.id || null,
          header_content: state.headerContent
            ? sanitizeContentForSave(state.headerContent)
            : null,
          team_confirmation_email_content: state.enableTeamRegistration
            ? sanitizeContentForSave(state.teamConfirmationEmailContent || '')
            : null,
          player_confirmation_email_content: state.enablePlayerRegistration
            ? sanitizeContentForSave(state.playerConfirmationEmailContent || '')
            : null,
          payment_processing_fee_bearer: state.paymentProcessingFeeBearer,
          payment_processing_fee_visible: state.paymentProcessingFeeVisible,
          league_processing_fee_percent: state.leagueProcessingFeeEnabled
            ? state.leagueProcessingFeePercent
            : null,
          league_processing_fee_flat: state.leagueProcessingFeeEnabled
            ? state.leagueProcessingFeeFlat
            : null,
          league_processing_fee_visible: state.leagueProcessingFeeVisible,
          refund_payment_processing_fee: state.refundPaymentProcessingFee,
        })
      } else {
        await createRegistration({
          scheduleId: scheduleId,
          allowPlayerRegistration: state.registrationType === 'player',
          registrationType: state.registrationType,
          enableRegistration: true,
          eventNumber: '',
          currency_country_id: state.currency_country_id,
          costPerRegistration: 0,
          paymentMethod: Number(state.payment_type),
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id: state?.player_form?.id || null,
          team_form_id: state?.team_form?.id || null,
          header_content: state.headerContent
            ? sanitizeContentForSave(state.headerContent)
            : null,
          team_confirmation_email_content: state.enableTeamRegistration
            ? sanitizeContentForSave(state.teamConfirmationEmailContent || '')
            : null,
          player_confirmation_email_content: state.enablePlayerRegistration
            ? sanitizeContentForSave(state.playerConfirmationEmailContent || '')
            : null,
          payment_processing_fee_bearer: state.paymentProcessingFeeBearer,
          payment_processing_fee_visible: state.paymentProcessingFeeVisible,
          league_processing_fee_percent: state.leagueProcessingFeeEnabled
            ? state.leagueProcessingFeePercent
            : null,
          league_processing_fee_flat: state.leagueProcessingFeeEnabled
            ? state.leagueProcessingFeeFlat
            : null,
          league_processing_fee_visible: state.leagueProcessingFeeVisible,
          refund_payment_processing_fee: state.refundPaymentProcessingFee,
        })
      }

      // Handle image upload/deletion
      if (state.customBackgroundImageChanged) {
        if (state.customBackgroundImage !== '') {
          await uploadRegistrationImage(scheduleId, state.customBackgroundImage)
        } else {
          await deleteRegistrationImage(scheduleId)
        }
      }
    } catch (error: any) {
      if (error?.invalid_fields) {
        setCustomErrors(error?.invalid_fields)
        throw error
      }
      const errorMessage = getErrorMessage(error)
      throw new Error(errorMessage)
    } finally {
      setState((prev) => ({ ...prev, loading: false }))
    }
  }

  const patchTeamForm = useCallback(
    (transform: (form: CustomForm) => CustomForm) => {
      setState((prev) =>
        prev.team_form
          ? { ...prev, team_form: transform(prev.team_form) }
          : prev
      )
    },
    []
  )

  const patchPlayerForm = useCallback(
    (transform: (form: CustomForm) => CustomForm) => {
      setState((prev) =>
        prev.player_form
          ? { ...prev, player_form: transform(prev.player_form) }
          : prev
      )
    },
    []
  )

  // PUT/POST/DELETE-only mutation helpers. Components apply the response via
  // patchTeamForm / patchPlayerForm; no redundant GET /forms/{id} is fired.
  const formMutations = useMemo(
    () => ({
      createGroup: async (data: any): Promise<Group> => {
        const body: any = {
          form_id: data.form_id,
          name: data.name,
          description: data.description,
          is_enabled: data.is_enabled ? 1 : 0,
          is_reserved: data.is_reserved ? 1 : 0,
          index: data.index,
        }
        if (data.is_content) {
          body.is_content = 1
          body.content = data.content || ''
        }
        const response = await req(`/forms/${data.form_id}/groups`, {
          method: 'POST',
          body: JSON.stringify(body),
        })
        return response.data as Group
      },
      updateGroup: async (groupId: string, data: any): Promise<Group> => {
        const body: any = { ...data }
        if (typeof data.is_enabled === 'boolean') {
          body.is_enabled = data.is_enabled ? 1 : 0
        }
        if (typeof data.is_reserved === 'boolean') {
          body.is_reserved = data.is_reserved ? 1 : 0
        }
        const response = await req(`/forms/groups/${groupId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })
        return response.data as Group
      },
      deleteGroup: async (groupId: string): Promise<void> => {
        await req(`/forms/groups/${groupId}`, { method: 'DELETE' })
      },
      reorderGroups: async (
        formId: string,
        groups: Array<{ id: string; index: number }>
      ): Promise<void> => {
        await req(`/forms/${formId}/groups/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ groups }),
        })
      },
      createItem: async (groupId: string, data: any): Promise<FormItem> => {
        const response = await req(`/forms/groups/${groupId}/items`, {
          method: 'POST',
          body: JSON.stringify({
            name: data.name,
            description: data.description,
            form_item_type_id: data.form_item_type_id,
            is_enabled: data.is_enabled ? 1 : 0,
            is_required: data.is_required ? 1 : 0,
            is_reserved: data.is_reserved ? 1 : 0,
            index: data.index,
            parameters: data.parameters,
          }),
        })
        return response.data as FormItem
      },
      updateItem: async (itemId: string, data: any): Promise<FormItem> => {
        const body: any = { ...data }
        if (typeof data.is_enabled === 'boolean') {
          body.is_enabled = data.is_enabled ? 1 : 0
        }
        if (typeof data.is_required === 'boolean') {
          body.is_required = data.is_required ? 1 : 0
        }
        if (typeof data.is_reserved === 'boolean') {
          body.is_reserved = data.is_reserved ? 1 : 0
        }
        const response = await req(`/forms/items/${itemId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })
        return response.data as FormItem
      },
      deleteItem: async (itemId: string): Promise<void> => {
        await req(`/forms/items/${itemId}`, { method: 'DELETE' })
      },
      reorderItems: async (
        groupId: string,
        items: Array<{ id: string; index: number }>
      ): Promise<void> => {
        await req(`/forms/groups/${groupId}/items/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ items }),
        })
      },
    }),
    []
  )

  const value: RegistrationSetupContextType = {
    state,
    setState,
    registrationTypes,
    availableTaxes,
    isUpdated,
    submitRegistration,
    apiWaivers,
    refetchWaivers,
    apiAccount,
    registrationId: registrationAdmin?.id || null,
    registrationAdmin,
    refetchRegistrationAdmin,
    isInitialLoading,
    hasRegistrations,
    setCustomErroByField,
    clearCustomErrors,
    createPlayerForm,
    createTeamForm,
    patchTeamForm,
    patchPlayerForm,
    formMutations,
    scheduleId,
    activeTab,
    setActiveTab,
  }

  // Hydrate the form from the admin payload. The mapping itself is a pure
  // helper (see helpers/hydrateFromAdmin) so it can be tested directly.
  useEffect(() => {
    if (!registrationAdmin?.id) {
      return
    }
    const hydrated = hydrateFromAdmin(registrationAdmin)
    setState((prevState) => ({
      ...prevState,
      ...hydrated,
      // A registration that doesn't exist yet has no provider of its own, so
      // fall back to the account default.
      payment_provider:
        hydrated.payment_provider ??
        apiAccount?.payment_provider_type?.id ??
        null,
    }))
  }, [registrationAdmin, apiAccount])

  return (
    <RegistrationSetupContext.Provider value={value}>
      {children}
    </RegistrationSetupContext.Provider>
  )
}

export { RegistrationSetupContextWrapper }
